/**
 * Member Login API Service
 * Handles traditional email/password authentication for members
 */

// Goes through the server-side proxy (src/app/api/proxy-member/[...path]/route.js)
// which injects the Basic credential — never sent from the client.
const MEMBERSHIP_API_BASE = '/api/proxy-member';

/**
 * Login member with email and password
 * @param {string} email - Member's email address
 * @param {string} password - Member's password
 * @param {string} hotelId - Optional hotel ID to restrict login to specific hotel
 * @param {string} turnstileToken - Cloudflare Turnstile verification token
 * @returns {Promise<Object>} Login response with member data
 */
export const loginMember = async (email, password, hotelId = null, turnstileToken = null) => {
  try {
    const requestBody = {
      email,
      password
    };

    // Add hotel_id if provided
    if (hotelId) {
      requestBody.hotel_id = hotelId;
    }
    
    // Add turnstile token if provided
    if (turnstileToken) {
      requestBody.turnstile_token = turnstileToken;
    }

    const response = await fetch(`${MEMBERSHIP_API_BASE}/members/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle different error cases
      if (response.status === 401) {
        throw new Error(data.message || 'Invalid email or password');
      } else if (response.status === 400) {
        throw new Error(data.message || 'Email and password are required');
      } else {
        throw new Error(data.message || 'Login failed');
      }
    }

    // Return successful response
    return {
      success: true,
      message: data.message,
      data: data.data
    };
  } catch (error) {
    console.error('Member login error:', error);
    return {
      success: false,
      message: error.message || 'Login failed',
      data: null
    };
  }
};

/**
 * Save member login session to localStorage
 * @param {Object} memberData - Member data from login response
 */
export const saveMemberLoginSession = (memberData) => {
  try {
    const sessionData = {
      email: memberData.email,
      memberData,
      loginTime: Date.now(),
      isManualLogin: true // Flag to indicate this is manual login, not OAuth
    };
    
    // Save session with property-specific key
    const sessionKey = `memberLogin_${memberData.hotel_id || 'default'}`;
    localStorage.setItem(sessionKey, JSON.stringify(sessionData));
    
    // Also save a general session for cross-property recognition
    localStorage.setItem('currentMemberLogin', JSON.stringify({
      email: memberData.email,
      currentProperty: memberData.hotel_id,
      loginTime: Date.now()
    }));
    
    console.log(`Saved member login session for ${memberData.email} at property ${memberData.hotel_id}`);
  } catch (error) {
    console.warn('Error saving member login session:', error);
  }
};

/**
 * Get member login session from localStorage
 * @param {string} propertyCode - Property code to get session for
 * @returns {Object|null} Member session data or null
 */
export const getMemberLoginSession = (propertyCode = 'default') => {
  try {
    const sessionKey = `memberLogin_${propertyCode}`;
    const sessionData = localStorage.getItem(sessionKey);
    
    if (!sessionData) return null;

    const parsed = JSON.parse(sessionData);
    
    // Check if session is less than 24 hours old (longer than OAuth session)
    const twentyFourHours = 24 * 60 * 60 * 1000;
    if (Date.now() - parsed.loginTime > twentyFourHours) {
      localStorage.removeItem(sessionKey);
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn('Error getting member login session:', error);
    localStorage.removeItem(`memberLogin_${propertyCode}`);
    return null;
  }
};

/**
 * Check if user is currently logged in via manual login
 * @returns {Object|null} Current login info or null
 */
export const getCurrentMemberLogin = () => {
  try {
    const loginData = localStorage.getItem('currentMemberLogin');
    if (!loginData) return null;

    const parsed = JSON.parse(loginData);
    
    // Check if session is still valid
    const twentyFourHours = 24 * 60 * 60 * 1000;
    if (Date.now() - parsed.loginTime > twentyFourHours) {
      localStorage.removeItem('currentMemberLogin');
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn('Error checking current member login:', error);
    localStorage.removeItem('currentMemberLogin');
    return null;
  }
};

/**
 * Clear member login session
 * @param {string} propertyCode - Property code to clear, or null for all
 */
export const clearMemberLoginSession = (propertyCode = null) => {
  try {
    if (propertyCode) {
      localStorage.removeItem(`memberLogin_${propertyCode}`);
    } else {
      // Clear all member login sessions
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('memberLogin_') || key === 'currentMemberLogin') {
          localStorage.removeItem(key);
        }
      });
    }
  } catch (error) {
    console.warn('Error clearing member login session:', error);
  }
};

/**
 * Logout member and clear all sessions
 */
export const logoutMember = () => {
  clearMemberLoginSession(); // Clear all sessions
  console.log('Member logged out successfully');
};